import React, { useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { toast } from "react-hot-toast";
import { tokens } from "../../Shared/tokens";
import { useLazyGetSinglePolicyQuery } from "../../features/policySlice";
import { usePaginationFromURL } from "../../hooks/usePaginationFromURL";

/** A policy created within this window gets a "New" badge. */
const NEW_POLICY_DAYS = 7;

const isPdf = (dataUrl = "") => dataUrl.startsWith("data:application/pdf");

/** Turn a base64 data URL into a real download without a network round trip. */
const downloadDataUrl = (dataUrl, filename) => {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const PoliciesDataTable = ({ policies = [], isLoading, filters }) => {
  const {
    currentPage: urlPage,
    pageSize,
    setPage: setUrlPage,
    setPageSize,
  } = usePaginationFromURL({ defaultPage: 1, defaultPageSize: 10 });

  const currentPage = urlPage - 1; // DataGrid pages are 0-indexed
  const setCurrentPage = (page) => setUrlPage(page >= 0 ? page + 1 : 1);

  const [viewing, setViewing] = useState(null); // { id, title }
  const [fetchPolicy, { data: openPolicy, isFetching: isFetchingDoc }] =
    useLazyGetSinglePolicyQuery();

  const rows = useMemo(() => {
    const query = filters.query.trim().toLowerCase();

    return policies
      .filter((policy) => {
        if (query && !policy?.policyHeading?.toLowerCase().includes(query)) {
          return false;
        }
        if (filters.year) {
          const date = new Date(policy?.effectiveFrom);
          if (String(date.getFullYear()) !== filters.year) return false;
        }
        return true;
      })
      .map((policy) => ({
        id: policy._id,
        policyTitle: policy?.policyHeading,
        effectiveFrom: new Date(policy?.effectiveFrom),
        createdDate: new Date(policy?.createdAt),
      }))
      // The API already sorts newest-first; sorting here too keeps the order
      // correct if the payload ever arrives unsorted.
      .sort((a, b) => b.createdDate - a.createdDate);
  }, [policies, filters]);

  const openDocument = useCallback(
    (row) => {
      setViewing({ id: row.id, title: row.policyTitle });
      fetchPolicy(row.id);
    },
    [fetchPolicy]
  );

  const closeDocument = useCallback(() => setViewing(null), []);

  useEffect(() => {
    if (!viewing) return undefined;
    const onKeyDown = (e) => e.key === "Escape" && closeDocument();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [viewing, closeDocument]);

  const handleDownload = useCallback(
    async (row) => {
      try {
        const policy = await fetchPolicy(row.id).unwrap();
        if (!policy?.fileAttachment) {
          toast.error("This policy has no document attached.");
          return;
        }
        const ext = isPdf(policy.fileAttachment) ? "pdf" : "docx";
        downloadDataUrl(policy.fileAttachment, `${row.policyTitle}.${ext}`);
      } catch {
        toast.error("Could not download the document.");
      }
    },
    [fetchPolicy]
  );

  const columns = useMemo(
    () => [
      {
        field: "policyTitle",
        headerName: "Policy",
        flex: 2,
        minWidth: 220,
        renderCell: ({ row }) => {
          const isNew =
            moment().diff(moment(row.createdDate), "days") < NEW_POLICY_DAYS;
          return (
            <TitleCell>
              <DescriptionOutlinedIcon fontSize="small" aria-hidden="true" />
              <span title={row.policyTitle}>{row.policyTitle}</span>
              {isNew && <NewBadge>New</NewBadge>}
            </TitleCell>
          );
        },
      },
      {
        field: "effectiveFrom",
        headerName: "Effective from",
        flex: 1,
        minWidth: 150,
        type: "date",
        // toDateString() rendered "Thu Dec 01 2022". moment is already a dep.
        renderCell: ({ row }) => (
          <span>{moment(row.effectiveFrom).format("D MMM YYYY")}</span>
        ),
      },
      {
        field: "createdDate",
        headerName: "Added",
        flex: 1,
        minWidth: 150,
        type: "date",
        renderCell: ({ row }) => (
          <TwoLine>
            <span>{moment(row.createdDate).format("D MMM YYYY")}</span>
            <small>{moment(row.createdDate).fromNow()}</small>
          </TwoLine>
        ),
      },
      {
        field: "actions",
        headerName: "Document",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Actions>
            <IconButton
              type="button"
              onClick={() => openDocument(row)}
              aria-label={`View ${row.policyTitle}`}
              title="View"
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              type="button"
              onClick={() => handleDownload(row)}
              aria-label={`Download ${row.policyTitle}`}
              title="Download"
            >
              <DownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Actions>
        ),
      },
    ],
    [openDocument, handleDownload]
  );

  const hasFilters = Boolean(filters.query || filters.year);
  const attachment = openPolicy?.fileAttachment;

  const ROW_H = 56;
  const HEADER_H = 44;
  const FOOTER_H = 52;
  // Empty and loading states need room to breathe; otherwise reserve exactly
  // the rows on this page, capped so long pages still scroll internally.
  const visibleRows = Math.min(rows.length, pageSize);
  const gridHeight =
    isLoading || visibleRows === 0
      ? 320
      : HEADER_H + visibleRows * ROW_H + FOOTER_H;

  return (
    <Card>
      <CardHeader>
        <h2>
          {isLoading ? "Loading policies…" : `${rows.length} `}
          {!isLoading && (rows.length === 1 ? "policy" : "policies")}
        </h2>
        {!isLoading && hasFilters && policies.length !== rows.length && (
          <FilteredNote>filtered from {policies.length}</FilteredNote>
        )}
      </CardHeader>

      <TableWrap $height={gridHeight}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          page={currentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(0);
          }}
          onPageChange={setCurrentPage}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={isLoading}
          pagination
          rowHeight={56}
          headerHeight={44}
          // Newest first on arrival, matching the API order.
          initialState={{
            sorting: { sortModel: [{ field: "createdDate", sort: "desc" }] },
          }}
          components={{
            NoRowsOverlay: () => (
              <EmptyState>
                {hasFilters ? (
                  <>
                    <SearchOffOutlinedIcon />
                    <strong>No policies match those filters</strong>
                    <span>Try a different title or clear the filters.</span>
                  </>
                ) : (
                  <>
                    <DescriptionOutlinedIcon />
                    <strong>No policies yet</strong>
                    <span>
                      Published policies will appear here, newest first.
                    </span>
                  </>
                )}
              </EmptyState>
            ),
          }}
          sx={gridSx}
        />
      </TableWrap>

      {viewing && createPortal(
        <Overlay
          onMouseDown={(e) => e.target === e.currentTarget && closeDocument()}
        >
          <Viewer role="dialog" aria-modal="true" aria-label={viewing.title}>
            <ViewerHeader>
              <h3>{viewing.title}</h3>
              <IconButton
                type="button"
                onClick={closeDocument}
                aria-label="Close document"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ViewerHeader>
            <ViewerBody>
              {isFetchingDoc && <Loading>Loading document…</Loading>}
              {!isFetchingDoc && attachment && isPdf(attachment) && (
                <iframe title={viewing.title} src={attachment} />
              )}
              {/* Browsers cannot render a .doc/.docx data URL — the old iframe
                  just showed an empty box for every Word policy. */}
              {!isFetchingDoc && attachment && !isPdf(attachment) && (
                <Fallback>
                  <DescriptionOutlinedIcon />
                  <strong>This is a Word document</strong>
                  <span>
                    Word files can't be previewed in the browser. Download it to
                    read it.
                  </span>
                  <DownloadButton
                    type="button"
                    onClick={() =>
                      downloadDataUrl(attachment, `${viewing.title}.docx`)
                    }
                  >
                    <DownloadOutlinedIcon fontSize="small" />
                    Download
                  </DownloadButton>
                </Fallback>
              )}
              {!isFetchingDoc && !attachment && (
                <Fallback>
                  <DescriptionOutlinedIcon />
                  <strong>No document attached</strong>
                </Fallback>
              )}
            </ViewerBody>
          </Viewer>
        </Overlay>,
        document.body
      )}
    </Card>
  );
};

const gridSx = {
  border: "none",
  fontSize: tokens.font.md,
  color: tokens.color.textPrimary,
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: tokens.color.surfaceSubtle,
    borderBottom: `1px solid ${tokens.color.border}`,
    borderRadius: 0,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: tokens.font.sm,
    fontWeight: 600,
    color: tokens.color.textSecondary,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${tokens.color.border}`,
    outline: "none !important",
  },
  "& .MuiDataGrid-row:hover": { backgroundColor: tokens.color.surfaceSubtle },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${tokens.color.border}`,
  },
  "& .MuiDataGrid-overlay": { backgroundColor: "transparent" },
};

const Card = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${tokens.color.surface};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.sm};
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${tokens.space.sm};
  padding: ${tokens.space.lg} ${tokens.space.xl};
  border-bottom: 1px solid ${tokens.color.border};

  h2 {
    margin: 0;
    font-size: ${tokens.font.lg};
    color: ${tokens.color.textPrimary};
  }
`;

const FilteredNote = styled.span`
  font-size: ${tokens.font.sm};
  color: ${tokens.color.textMuted};
`;

/* Height follows the row count instead of a fixed 80vh, so a two-row list
   doesn't leave several hundred pixels of empty grid below it. */
const TableWrap = styled.div`
  width: 100%;
  height: ${(p) => p.$height}px;
`;

const TitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.space.sm};
  min-width: 0;

  svg {
    color: ${tokens.color.textMuted};
    flex-shrink: 0;
  }

  span {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const NewBadge = styled.span`
  flex-shrink: 0;
  padding: 2px ${tokens.space.sm};
  font-size: ${tokens.font.xs};
  font-weight: 600;
  color: ${tokens.color.success};
  background-color: ${tokens.color.successSoft};
  border-radius: ${tokens.radius.pill};
`;

const TwoLine = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.35;

  small {
    font-size: ${tokens.font.xs};
    color: ${tokens.color.textMuted};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${tokens.space.xs};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: ${tokens.color.textMuted};
  background: none;
  border: 1px solid transparent;
  border-radius: ${tokens.radius.sm};
  cursor: pointer;
  outline: none;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease},
    color ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    background-color: ${tokens.color.primarySoft};
    color: ${tokens.color.primary};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const EmptyState = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${tokens.space.sm};
  padding: ${tokens.space["2xl"]};
  text-align: center;

  svg {
    font-size: 32px;
    color: ${tokens.color.borderStrong};
  }

  strong {
    font-size: ${tokens.font.lg};
    color: ${tokens.color.textPrimary};
  }

  span {
    font-size: ${tokens.font.md};
    color: ${tokens.color.textMuted};
  }
`;

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background-color: rgba(17, 24, 39, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${tokens.space.xl};
  animation: ${fadeIn} ${tokens.motion.base} ${tokens.motion.ease};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Viewer = styled.div`
  width: min(1000px, 100%);
  height: min(90vh, 100%);
  display: flex;
  flex-direction: column;
  background-color: ${tokens.color.surface};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.overlay};
  overflow: hidden;
`;

const ViewerHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.space.lg};
  padding: ${tokens.space.md} ${tokens.space.lg};
  border-bottom: 1px solid ${tokens.color.border};

  h3 {
    margin: 0;
    font-size: ${tokens.font.lg};
    color: ${tokens.color.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ViewerBody = styled.div`
  flex: 1;
  min-height: 0;
  background-color: ${tokens.color.surfaceHover};

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const Loading = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${tokens.font.md};
  color: ${tokens.color.textMuted};
`;

const Fallback = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${tokens.space.md};
  padding: ${tokens.space["2xl"]};
  text-align: center;

  > svg {
    font-size: 40px;
    color: ${tokens.color.borderStrong};
  }

  strong {
    font-size: ${tokens.font.lg};
    color: ${tokens.color.textPrimary};
  }

  span {
    max-width: 34ch;
    font-size: ${tokens.font.md};
    line-height: 1.5;
    color: ${tokens.color.textMuted};
  }
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.space.sm};
  min-height: 40px;
  padding: 0 ${tokens.space.lg};
  font-size: ${tokens.font.md};
  font-weight: 600;
  color: ${tokens.color.onPrimary};
  background-color: ${tokens.color.primary};
  border: none;
  border-radius: ${tokens.radius.md};
  cursor: pointer;
  outline: none;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    background-color: ${tokens.color.primaryHover};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;
